import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Filter,
  MessageCircle,
  Send as SendIcon,
  Instagram,
  Users,
  Clock,
  Radio,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusDot } from "@/components/shared/StatusDot";
import { EmptyState } from "@/components/shared/EmptyState";
import { cn, platformLabel, formatNumber } from "@/lib/utils";
import type { Channel, ChannelPlatform } from "@/types";

const platformIcons: Record<ChannelPlatform, typeof MessageCircle> = {
  whatsapp: MessageCircle,
  telegram: SendIcon,
  instagram: Instagram,
};

const platformColors: Record<ChannelPlatform, string> = {
  whatsapp: "text-emerald-400",
  telegram: "text-sky-400",
  instagram: "text-pink-400",
};

// Mock data
const mockChannels: Channel[] = [
  {
    id: "1",
    name: "Ofertas SP Capital",
    platform: "whatsapp",
    member_count: 256,
    status: "connected",
    last_dispatch_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    agent_enabled: true,
    avatar_url: null,
    tenant_id: "t1",
  },
  {
    id: "2",
    name: "Promos RJ",
    platform: "whatsapp",
    member_count: 189,
    status: "connected",
    last_dispatch_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    agent_enabled: false,
    avatar_url: null,
    tenant_id: "t1",
  },
  {
    id: "3",
    name: "Vendas Diretas",
    platform: "telegram",
    member_count: 432,
    status: "connected",
    last_dispatch_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    agent_enabled: true,
    avatar_url: null,
    tenant_id: "t1",
  },
  {
    id: "4",
    name: "Flash Deals",
    platform: "telegram",
    member_count: 87,
    status: "disconnected",
    last_dispatch_at: null,
    agent_enabled: false,
    avatar_url: null,
    tenant_id: "t1",
  },
  {
    id: "5",
    name: "Loja Oficial",
    platform: "instagram",
    member_count: 1240,
    status: "connected",
    last_dispatch_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    agent_enabled: true,
    avatar_url: null,
    tenant_id: "t1",
  },
  {
    id: "6",
    name: "Outlet MG",
    platform: "whatsapp",
    member_count: 312,
    status: "error",
    last_dispatch_at: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    agent_enabled: false,
    avatar_url: null,
    tenant_id: "t1",
  },
];

function ChannelCard({ channel, index }: { channel: Channel; index: number }) {
  const [agentEnabled, setAgentEnabled] = useState(channel.agent_enabled);
  const PlatformIcon = platformIcons[channel.platform];
  const platformColor = platformColors[channel.platform];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <Card className="group transition-all duration-200 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            {/* Left: Channel info */}
            <div className="flex items-start gap-3.5 min-w-0">
              <div
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary",
                  platformColor
                )}
              >
                <PlatformIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground truncate">
                    {channel.name}
                  </h3>
                  <StatusDot status={channel.status} />
                </div>
                <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {formatNumber(channel.member_count)}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {platformLabel(channel.platform)}
                  </Badge>
                </div>
                {channel.last_dispatch_at && (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground/70">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(channel.last_dispatch_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Agent toggle */}
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <Switch
                checked={agentEnabled}
                onCheckedChange={setAgentEnabled}
                aria-label={`Agente AI para ${channel.name}`}
              />
              <span className="text-[10px] text-muted-foreground">
                {agentEnabled ? "AI On" : "AI Off"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function ChannelsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("all");

  const filteredChannels = useMemo(() => {
    return mockChannels.filter((ch) => {
      if (activeTab !== "all" && ch.platform !== activeTab) return false;
      if (statusFilter !== "all" && ch.status !== statusFilter) return false;
      if (search && !ch.name.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [activeTab, statusFilter, search]);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="all">
              Todos
              <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                {mockChannels.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="whatsapp">
              <MessageCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-400" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="telegram">
              <SendIcon className="mr-1.5 h-3.5 w-3.5 text-sky-400" />
              Telegram
            </TabsTrigger>
            <TabsTrigger value="instagram">
              <Instagram className="mr-1.5 h-3.5 w-3.5 text-pink-400" />
              Instagram
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Filters */}
        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar canais..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="connected">Conectados</SelectItem>
              <SelectItem value="disconnected">Desconectados</SelectItem>
              <SelectItem value="error">Com erro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Channel Grid */}
        <TabsContent value={activeTab} className="mt-0">
          {filteredChannels.length === 0 ? (
            <EmptyState
              icon={Radio}
              title="Nenhum canal encontrado"
              description="Conecte seu primeiro canal para comecar a enviar disparos automatizados."
              action={{
                label: "Conectar Canal",
                onClick: () => {},
              }}
            />
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filteredChannels.map((channel, i) => (
                <ChannelCard key={channel.id} channel={channel} index={i} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
